import {Banner, Button, CalloutIcon, DotProgress} from '@wandb/components';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useMemo} from 'react';

type VirtuosoContext = {
  isFooterShown: boolean;
  isLoading: boolean;
  initialError: boolean;
  loadMoreError: boolean;
  retryInitialFetch?: () => void;
};

/**
 * This component is used as Header/Footer on the virtuoso list to ensure
 * proper top/bottom spacing. Unfortunately I wasn't able to figure out a
 * plain CSS solution that played nicely with Virtuoso. My first instinct
 * was to add top/bottom padding to the Scroller container, but that didn't
 * affect the position of the inner items. Then, I tried adding top/bottom
 * margin to the first/last items in the list. The first item correctly
 * displayed a top margin, but the last item never showed its bottom margin.
 * Next, I tried setting top/bottom padding on a custom List component,
 * but then the scroll stopped working. Finally I resorted to this solution.
 * Though unideal, it's the only option I found that actually works without
 * breaking something else.
 * Related: https://github.com/petyosi/react-virtuoso/issues/238
 */
export const TopBottomSpacing = () => <div className="h-8" />;

export const EmptyPlaceholder = ({context}: {context: VirtuosoContext}) => {
  return (
    <div className="flex h-[calc(100%_-_16px)] w-full flex-col items-center justify-center text-wb-text-tertiary">
      {context?.isLoading ? (
        <DotProgress size="lg" aria-label="Loading keys" />
      ) : context?.initialError ? (
        <div className="flex flex-col items-center gap-12">
          <CalloutIcon icon="warning" color="red" size="md" />
          <p className="font-semibold">Couldn’t load keys</p>
          <Button
            aria-label="Retry loading keys"
            onPress={context.retryInitialFetch}
            startIcon="reload-refresh"
            variant="secondary">
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-16 flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-moon-300/[0.48]">
            <Icon name="search" className="h-24 w-24" />
          </div>
          <p className="font-semibold">No results found</p>
          <p className="text-sm">Try another search.</p>
        </>
      )}
    </div>
  );
};

const Footer = ({context}: {context: VirtuosoContext}) => {
  if (context?.loadMoreError) {
    return (
      <div className="w-full px-12 py-8">
        <Banner
          aria-label="Failed to load more keys"
          defaultVisible
          isDismissible={false}
          size="md"
          variant="error">
          <Banner.Body>Couldn’t load more keys.</Banner.Body>
        </Banner>
      </div>
    );
  }

  return <TopBottomSpacing />;
};

/**
 * Creates cached components/context for the Virtuoso list in SingleKeyPanelAdd
 */
export function useVirtuosoComponents({
  isFooterShown,
  isLoading = false,
  initialError = false,
  loadMoreError = false,
  retryInitialFetch,
}: {
  isFooterShown: boolean;
  isLoading?: boolean;
  initialError?: boolean;
  loadMoreError?: boolean;
  retryInitialFetch?: () => void;
}) {
  const components = useMemo(
    () => ({
      EmptyPlaceholder,
      Header: TopBottomSpacing,
      Footer,
    }),
    []
  );

  const context = useMemo(
    () => ({
      isFooterShown,
      isLoading,
      initialError,
      loadMoreError,
      retryInitialFetch,
    }),
    [initialError, isFooterShown, isLoading, loadMoreError, retryInitialFetch]
  );

  return useMemo(
    () => ({
      components,
      context,
    }),
    [components, context]
  );
}
